import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import Toast from "./Toast";
import LiveSlideshow from "./LiveSlideshow";
import api, { API_URL } from "../api";
import { templates } from "./InvitationTemplates";

const EventManagement = ({
  event,
  onBack,
  isEditing,
  setIsEditing,
  updateEventDetails,
  setEventDetails,
  user
}) => {
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [pendingMedia, setPendingMedia] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("approved");
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const fetchMedia = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [approvedRes, pendingRes] = await Promise.all([
          api.get(`/api/media/event/${event._id || event.id}?status=approved`),
          api.get(`/api/media/event/${event._id || event.id}?status=archived`),
        ]);
        setMedia(approvedRes.data);
        setPendingMedia(pendingRes.data);

        if (event.rsvpEnabled) {
          const rsvpRes = await api.get(`/api/events/${event._id || event.id}/rsvp`);
          setRsvps(rsvpRes.data);
        }

        const anaRes = await api.get(`/api/events/${event._id || event.id}/analytics`);
        setAnalytics(anaRes.data);
      } catch (err) {
        setToast({ type: "danger", message: "فشل تحميل الصور." });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [event._id, event.id]
  );

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const eventUrl =
    process.env.REACT_APP_CLIENT_URL + `/event/${event._id || event.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setToast({
        message: "اللينك اتنسخ! ابعته للناس اللي عايزة ترفع صور.",
        type: "success",
      });
    } catch {
      setToast({ message: "حصل مشكلة في نسخ اللينك.", type: "danger" });
    }
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(
      `شاركنا أجمل لحظات ${event.name} في الألبوم ده: ${eventUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const uploadImage = async (isEventPhoto = false) => {
    if (!selectedFile) {
      setToast({ type: "warning", message: "اختر صورة للرفع" });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("eventId", event._id || event.id);
    formData.append("guestName", "المنظم");
    formData.append("type", "photo");

    try {
      setLoading(true);
      const res = await api.post("/api/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelectedFile(null);

      if (isEventPhoto) {
        const photoUrl = res.data.url;
        await api.put(`/api/events/${event._id || event.id}`, {
          eventPhoto: photoUrl,
        });
        setEventDetails({ ...event, eventPhoto: photoUrl });
        setToast({ type: "success", message: "تم تحديث صورة الحدث بنجاح." });
      } else {
        setToast({ type: "success", message: "تم رفع الصورة بنجاح." });
      }
      fetchMedia(true);
    } catch (err) {
      console.error("Upload Error:", err);
      const serverMessage =
        err.response?.data?.error || err.response?.data?.message || "";
      setToast({
        type: "danger",
        message: `فشل رفع الصورة: ${serverMessage || "حاول تاني"}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteMedia = async (id) => {
    if (!window.confirm("هل تريد حذف الصورة نهائياً؟")) return;
    try {
      await api.delete(`/api/media/${id}`);
      setToast({ type: "info", message: "تم حذف الصورة." });
      fetchMedia(true);
    } catch {
      setToast({ type: "danger", message: "فشل حذف الصورة." });
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/api/media/${id}/status`, { status });
      setToast({ type: "success", message: `تم تحديث حالة المشاركة بنجاح.` });
      fetchMedia(true);
    } catch {
      setToast({ type: "danger", message: "فشل تحديث الحالة." });
    }
  };

  const handleBatchStatusUpdate = async (status) => {
    if (selectedMediaIds.length === 0) return;
    setBatchLoading(true);
    try {
      await api.patch("/api/media/bulk-status", {
        ids: selectedMediaIds,
        status,
      });
      setToast({
        type: "success",
        message: "تم تحديث حالة المجموعة المختارة.",
      });
      setSelectedMediaIds([]);
      fetchMedia(true);
    } catch (err) {
      setToast({ type: "danger", message: "فشل تحديث المجموعة." });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedMediaIds.length === 0) return;
    if (!window.confirm(`هل أنت متأكد من حذف ${selectedMediaIds.length} عنصر؟`))
      return;
    setBatchLoading(true);
    try {
      await api.delete("/api/media/bulk-delete", {
        data: { ids: selectedMediaIds },
      });
      setToast({ type: "info", message: "تم حذف المجموعة المختارة." });
      setSelectedMediaIds([]);
      fetchMedia(true);
    } catch (err) {
      setToast({ type: "danger", message: "فشل حذف المجموعة." });
    } finally {
      setBatchLoading(false);
    }
  };

  const toggleSelectMedia = (id) => {
    setSelectedMediaIds((prev) =>
      prev.includes(id) ? prev.filter((mid) => mid !== id) : [...prev, id]
    );
  };

  const handleDownloadZip = () => {
    const token = localStorage.getItem("youmak_token");
    window.open(
      `${API_URL}/api/media/download/${event._id || event.id}?token=${token}`,
      "_blank"
    );
  };

  return (
    <motion.div
      dir="rtl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ type: "spring", damping: 20, stiffness: 100 }}
    >
      {/* ── EVENT HERO BANNER ── */}
      <div
        className="event-hero-banner position-relative overflow-hidden rounded-celebration mb-4"
        style={{ minHeight: "280px" }}
      >
        {/* Background blur layer */}
        {event.eventPhoto ? (
          <>
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${event.eventPhoto})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(20px) brightness(0.4) saturate(1.2)",
                transform: "scale(1.1)",
                zIndex: 0,
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(135deg, rgba(139,73,62,0.7) 0%, rgba(45,30,23,0.8) 100%)",
                zIndex: 1,
              }}
            />
          </>
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, var(--primary) 0%, #2d1e17 100%)",
              zIndex: 0,
            }}
          />
        )}

        {/* Content */}
        <div
          className="position-relative d-flex flex-column flex-md-row align-items-center gap-4 p-4 p-md-5"
          style={{ zIndex: 2 }}
        >
          {/* Portrait thumbnail */}
          {event.eventPhoto && (
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              style={{
                width: "120px",
                height: "160px",
                borderRadius: "16px",
                overflow: "hidden",
                border: "3px solid rgba(255,255,255,0.35)",
                boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
                flexShrink: 0,
              }}
            >
              <img
                src={event.eventPhoto}
                alt={event.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </motion.div>
          )}

          {/* Text info */}
          <div className="flex-grow-1 text-white text-end">
            <div className="d-flex align-items-center gap-2 justify-content-end mb-2 flex-wrap">
              <span
                className={`badge rounded-pill px-3 py-1 fw-bold ${
                  event.isApproved
                    ? "bg-success text-white"
                    : "bg-warning text-dark"
                }`}
                style={{ fontSize: "0.7rem" }}
              >
                {event.isApproved ? "نشط ✅" : "قيد المراجعة ⏳"}
              </span>
              <span
                style={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.1em",
                  opacity: 0.65,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                ألبوم المناسبة
              </span>
            </div>

            <h1
              className="fw-black mb-2"
              style={{
                fontFamily: "var(--font-headline)",
                fontSize: "clamp(1.4rem, 4vw, 2.2rem)",
                lineHeight: 1.2,
                textShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
            >
              {event.name}
            </h1>

            {event.description && (
              <p
                className="mb-3 opacity-80 arabic-leading"
                style={{ fontSize: "0.95rem", maxWidth: "500px" }}
              >
                {event.description}
              </p>
            )}

            <div
              className="d-flex flex-wrap gap-3 justify-content-end"
              style={{ fontSize: "0.82rem", opacity: 0.85 }}
            >
              {event.clientName && (
                <span className="d-flex align-items-center gap-1">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1rem" }}
                  >
                    person
                  </span>
                  {event.clientName}
                </span>
              )}
              {event.eventDate && (
                <span className="d-flex align-items-center gap-1">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1rem" }}
                  >
                    calendar_month
                  </span>
                  {new Date(event.eventDate).toLocaleDateString("ar-EG", {
                    dateStyle: "medium",
                  })}
                </span>
              )}
              {event.locationName && (
                <span className="d-flex align-items-center gap-1">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1rem" }}
                  >
                    location_on
                  </span>
                  {event.locationGoogleMapsLink ? (
                    <a
                      href={event.locationGoogleMapsLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white text-decoration-none fw-bold"
                    >
                      {event.locationName}
                    </a>
                  ) : (
                    event.locationName
                  )}
                </span>
              )}
            </div>
          </div>

          {/* QR Code */}
          <div className="d-none d-lg-flex flex-column align-items-center gap-2 flex-shrink-0">
            {event.isApproved ? (
              <motion.div
                className="bg-white p-3 rounded-4 shadow-lg"
                whileHover={{ scale: 1.05 }}
              >
                <QRCode value={eventUrl} size={110} />
                <p className="mt-2 mb-0 text-center fw-bold text-primary small">
                  كود المشاركة
                </p>
              </motion.div>
            ) : (
              <div
                className="bg-white bg-opacity-10 border border-white border-opacity-25 rounded-4 d-flex align-items-center justify-content-center text-white text-center p-3"
                style={{
                  width: "140px",
                  height: "140px",
                  fontSize: "0.75rem",
                  opacity: 0.7,
                }}
              >
                QR بانتظار التفعيل
              </div>
            )}
          </div>
        </div>

        {/* Action bar */}
        <div
          className="position-relative d-flex flex-wrap gap-2 px-4 pb-4 justify-content-end"
          style={{ zIndex: 2 }}
        >
          <button
            className="btn btn-sm fw-bold rounded-pill px-3 py-2"
            style={{
              background: "rgba(255,255,255,0.15)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.25)",
              backdropFilter: "blur(8px)",
            }}
            onClick={onBack}
          >
            ← رجوع
          </button>
          <button
            className="btn btn-sm fw-bold rounded-pill px-3 py-2"
            style={{
              background: "rgba(255,255,255,0.15)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.25)",
              backdropFilter: "blur(8px)",
            }}
            onClick={() => fetchMedia(false)}
          >
            🔄 تحديث
          </button>
          <button
            className="btn btn-sm fw-bold rounded-pill px-3 py-2"
            style={{
              background: "rgba(255,255,255,0.15)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.25)",
              backdropFilter: "blur(8px)",
            }}
            onClick={() => navigate(`/event-book/${event._id || event.id}`)}
          >
            📖 الكتاب
          </button>
          <button
            className="btn btn-sm fw-bold rounded-pill px-3 py-2"
            style={{
              background: "rgba(255,255,255,0.15)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.25)",
              backdropFilter: "blur(8px)",
            }}
            onClick={() => setShowSlideshow(true)}
          >
            📺 العرض
          </button>
          <button
            className="btn btn-sm fw-bold rounded-pill px-3 py-2"
            style={{
              background: "rgba(255,255,255,0.9)",
              color: "var(--primary)",
              border: "none",
            }}
            onClick={handleDownloadZip}
          >
            📦 تحميل ZIP
          </button>
          <button
            className="btn btn-sm fw-bold rounded-pill px-3 py-2"
            style={{
              background: "rgba(255,255,255,0.15)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.25)",
              backdropFilter: "blur(8px)",
            }}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? "❌ إلغاء" : "✏️ تعديل"}
          </button>
        </div>
      </div>

      {/* ── EDIT FORM ── */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-celebration shadow-sm border border-primary-dim p-4 p-md-5 mb-4 text-end overflow-hidden"
          >
            <h5 className="fw-black mb-4 text-primary">
              تعديل تفاصيل المناسبة
            </h5>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-bold small text-muted">
                  اسم المناسبة
                </label>
                <input
                  className="form-control"
                  value={event.name}
                  onChange={(e) =>
                    setEventDetails({ ...event, name: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">
                  اسم العميل
                </label>
                <input
                  className="form-control"
                  value={event.clientName || ""}
                  onChange={(e) =>
                    setEventDetails({ ...event, clientName: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">
                  تاريخ الحدث
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={
                    event.eventDate
                      ? new Date(event.eventDate).toISOString().split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setEventDetails({ ...event, eventDate: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-bold small text-muted">
                  وصف المناسبة
                </label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={event.description || ""}
                  onChange={(e) =>
                    setEventDetails({ ...event, description: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">
                  اسم المكان
                </label>
                <input
                  className="form-control"
                  value={event.locationName || ""}
                  onChange={(e) =>
                    setEventDetails({ ...event, locationName: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted">
                  رابط الخريطة
                </label>
                <input
                  className="form-control"
                  value={event.locationGoogleMapsLink || ""}
                  onChange={(e) =>
                    setEventDetails({
                      ...event,
                      locationGoogleMapsLink: e.target.value,
                    })
                  }
                />
              </div>
              {(user?.plan === 'pro' || user?.plan === 'corporate' || user?.role === 'admin') && (
                <div className="col-12">
                  <div className="form-check form-switch d-flex align-items-center gap-3 justify-content-end p-0">
                    <label className="form-check-label fw-bold small text-muted mb-0">تفعيل نظام RSVP</label>
                    <input
                      className="form-check-input ms-0"
                      type="checkbox"
                      role="switch"
                      checked={event.rsvpEnabled || false}
                      onChange={(e) => {
                        setEventDetails({ ...event, rsvpEnabled: e.target.checked });
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="col-md-4">
                <label className="form-label fw-bold small text-muted">الثيم (Theme)</label>
                <select
                  className="form-select"
                  value={event.theme || "wedding"}
                  onChange={(e) => setEventDetails({ ...event, theme: e.target.value })}
                >
                  <option value="wedding">فرح (Wedding)</option>
                  <option value="birthday">عيد ميلاد (Birthday)</option>
                  <option value="corporate">شركة (Corporate)</option>
                  <option value="graduation">تخرج (Graduation)</option>
                  <option value="baby_shower">سبوع (Baby Shower)</option>
                  <option value="anniversary">ذكرى سنوية (Anniversary)</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold small text-muted">ستايل الغلاف</label>
                <select
                  className="form-select"
                  value={event.coverStyle || "blur"}
                  onChange={(e) => setEventDetails({ ...event, coverStyle: e.target.value })}
                >
                  <option value="blur">ضبابي (Blur)</option>
                  <option value="dark">داكن (Dark)</option>
                  <option value="gradient">تدرج (Gradient)</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold small text-muted">لون التمييز</label>
                <input
                  type="color"
                  className="form-control form-control-color w-100"
                  value={event.accentColor || "#8b493e"}
                  onChange={(e) => setEventDetails({ ...event, accentColor: e.target.value })}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-bold small text-muted">كود اللبس (Dress Code)</label>
                <input
                  className="form-control"
                  placeholder="مثلاً: Casual, Formal, Semi-Formal..."
                  value={event.dressCode || ""}
                  onChange={(e) => setEventDetails({ ...event, dressCode: e.target.value })}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-bold small text-muted">كلمة سر الألبوم (اختياري)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="سيبها فاضية لو مش عايز كلمة سر"
                  onChange={(e) => setEventDetails({ ...event, password: e.target.value })}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-bold small text-muted">
                  تحديث صورة الحدث (البانر)
                </label>
                <div className="d-flex gap-2 align-items-start flex-wrap">
                  {event.eventPhoto && (
                    <div
                      className="rounded-3 overflow-hidden border border-primary-dim shadow-sm flex-shrink-0"
                      style={{ width: "80px", height: "80px" }}
                    >
                      <img
                        src={event.eventPhoto}
                        alt="Current"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-grow-1">
                    <input
                      type="file"
                      className="form-control mb-2"
                      accept="image/*"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                    />
                    <button
                      className="btn btn-outline-primary btn-sm rounded-pill fw-bold px-4"
                      onClick={() => uploadImage(true)}
                      disabled={!selectedFile}
                    >
                      رفع وتحديث البانر
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-12 text-center pt-2">
                <button
                  className="btn btn-primary px-5 fw-bold rounded-pill shadow-sm"
                  onClick={updateEventDetails}
                >
                  حفظ التغييرات ✅
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── SHARE / ACTIVATION SECTION ── */}
      <div className="bg-white rounded-celebration shadow-sm border border-primary-dim p-4 p-md-5 mb-4">
        {event.isApproved ? (
          <>
            <h5 className="fw-black mb-4 text-end">مشاركة الألبوم مع الضيوف</h5>
            <div className="d-flex gap-2 gap-md-3 flex-column flex-md-row mb-4 justify-content-center justify-content-md-end">
              <motion.button
                className="btn btn-primary btn-lg fw-bold px-4 px-md-5 rounded-pill shadow-lg"
                onClick={copyLink}
                whileTap={{ scale: 0.95 }}
              >
                📋 نسخ لينك الضيوف
              </motion.button>
              <motion.button
                className="btn btn-outline-primary btn-lg fw-bold px-4 px-md-5 rounded-pill shadow-sm d-flex align-items-center gap-2 justify-content-center"
                onClick={shareToWhatsApp}
                whileTap={{ scale: 0.95 }}
              >
                💬 واتساب
              </motion.button>
            </div>
            <div className="p-3 bg-surface-container-low rounded-3 border-start border-primary border-4 text-end shadow-sm">
              <p className="small text-muted mb-1 fw-bold">
                رابط الألبوم للضيوف:
              </p>
              <a
                href={eventUrl}
                target="_blank"
                rel="noreferrer"
                className="d-block fw-bold text-decoration-none text-primary small"
                style={{ wordBreak: "break-all" }}
              >
                {eventUrl} 🔗
              </a>
            </div>

            {/* Mobile QR */}
            <div className="d-lg-none text-center mt-4">
              <div className="bg-white p-3 rounded-4 shadow-sm border border-primary-dim d-inline-block">
                <QRCode value={eventUrl} size={120} />
                <p className="mt-2 mb-0 fw-bold text-primary small">
                  كود المشاركة
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="alert alert-warning border-0 shadow-sm p-4 rounded-4 text-end mb-0">
            <h4 className="fw-bold mb-3">⚠️ المناسبة لسه متفعلتش!</h4>
            <p className="mb-4">
              تواصل مع الإدارة لتفعيل المناسبة وبدء استقبال الصور.
            </p>
            <div className="bg-white p-3 rounded shadow-sm d-inline-block text-end">
              <p className="mb-1">
                💬 واتساب: <strong>01013201998</strong>
              </p>
              <p className="mb-0">
                📧 إيميل: <strong>youmak.egp@gmail.com</strong>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── ADD MEDIA ── */}
      <div className="card shadow-sm mb-4 border-0 bg-white border-top border-primary border-5 rounded-celebration">
        <div className="card-body p-3 p-md-4">
          <h5 className="fw-black mb-3 text-end text-on-surface">
            إضافة صور جديدة كمنظم
          </h5>
          <div className="d-flex flex-column flex-md-row gap-2">
            <input
              type="file"
              className="form-control form-control-lg flex-grow-1"
              accept="image/*,video/*"
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />
            <motion.button
              className="btn btn-primary px-4 px-md-5 py-2 py-md-0 fw-bold flex-shrink-0 rounded-pill"
              onClick={() => uploadImage(false)}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? "جاري..." : "رفع الصورة"}
            </motion.button>
          </div>
        </div>
      </div>

      {/* ── SLIDESHOW ── */}
      <AnimatePresence>
        {showSlideshow && (
          <LiveSlideshow
            eventId={event._id || event.id}
            onClose={() => setShowSlideshow(false)}
          />
        )}
      </AnimatePresence>

      {/* ── BULK ACTIONS ── */}
      <AnimatePresence>
        {selectedMediaIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed-bottom bg-white border-top shadow-lg p-3 d-flex justify-content-center gap-3 align-items-center rounded-top-5"
            style={{ zIndex: 1050 }}
          >
            <span className="fw-bold text-primary ms-3">
              تم اختيار {selectedMediaIds.length} عنصر
            </span>
            <button
              className="btn btn-sm btn-warning rounded-pill px-3 px-md-4 fw-bold"
              onClick={() =>
                handleBatchStatusUpdate(
                  activeTab === "approved" ? "archived" : "approved"
                )
              }
              disabled={batchLoading}
            >
              {batchLoading
                ? "..."
                : activeTab === "approved"
                ? "أرشفة"
                : "استعادة"}
            </button>
            <button
              className="btn btn-sm btn-danger rounded-pill px-3 px-md-4 fw-bold"
              onClick={handleBatchDelete}
              disabled={batchLoading}
            >
              {batchLoading ? "..." : "حذف"}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill px-3 px-md-4"
              onClick={() => setSelectedMediaIds([])}
            >
              إلغاء
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── TABS ── */}
      <ul
        className="nav nav-pills mb-4 justify-content-center bg-surface-variant p-2 rounded-pill shadow-sm border mx-auto"
        style={{ width: "fit-content" }}
      >
        <li className="nav-item">
          <button
            className={`nav-link fw-bold px-3 px-md-4 rounded-pill ${
              activeTab === "approved" ? "active" : ""
            }`}
            onClick={() => setActiveTab("approved")}
          >
            المنشورة ({media.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link fw-bold px-3 px-md-4 rounded-pill ${
              activeTab === "archived" ? "active" : "text-primary"
            }`}
            onClick={() => setActiveTab("archived")}
          >
            المؤرشفة ({pendingMedia.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link fw-bold px-3 px-md-4 rounded-pill ${
              activeTab === "invitation" ? "active" : "text-primary"
            }`}
            onClick={() => setActiveTab("invitation")}
          >
            كارت الدعوة 🎫
          </button>
        </li>
        {(user?.plan === 'pro' || user?.plan === 'corporate' || user?.role === 'admin') && (
          <>
            <li className="nav-item">
              <button
                className={`nav-link fw-bold px-3 px-md-4 rounded-pill ${
                  activeTab === "rsvp" ? "active" : "text-primary"
                }`}
                onClick={() => setActiveTab("rsvp")}
              >
                RSVP 👥
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link fw-bold px-3 px-md-4 rounded-pill ${
                  activeTab === "analytics" ? "active" : "text-primary"
                }`}
                onClick={() => setActiveTab("analytics")}
              >
                تحليلات 📈
              </button>
            </li>
          </>
        )}
      </ul>

      {/* ── MEDIA GRID ── */}
      <div className="main-content-area">
        {activeTab === "analytics" ? (
          <div className="analytics-view py-4 px-2 text-end">
             <h4 className="fw-black mb-4 font-headline">تحليلات المناسبة 📈</h4>
             <div className="row g-4">
                <div className="col-md-3">
                   <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                      <p className="text-muted small fw-bold mb-1">إجمالي الصور 📸</p>
                      <h2 className="fw-black text-primary mb-0">{analytics?.total || 0}</h2>
                   </div>
                </div>
                <div className="col-md-3">
                   <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                      <p className="text-muted small fw-bold mb-1">التفاعلات ❤️</p>
                      <h2 className="fw-black text-primary mb-0">{analytics?.totalReactions || 0}</h2>
                   </div>
                </div>
                <div className="col-md-3">
                   <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                      <p className="text-muted small fw-bold mb-1">الضيوف الفريدين 👥</p>
                      <h2 className="fw-black text-primary mb-0">{analytics?.uniqueUploaders || 0}</h2>
                   </div>
                </div>
                <div className="col-md-3">
                   <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                      <p className="text-muted small fw-bold mb-1">أكثر مشارك 🏆</p>
                      <h2 className="fw-bold text-primary mb-0" style={{ fontSize: '1.2rem' }}>{analytics?.topContributor?.name || "لا يوجد"}</h2>
                      <span className="small text-muted">{analytics?.topContributor?.count || 0} صورة</span>
                   </div>
                </div>

                {/* RSVP Stats in Analytics */}
                {event.rsvpEnabled && analytics?.rsvpStats && (
                  <>
                    <div className="col-md-3">
                      <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                        <p className="text-muted small fw-bold mb-1">إجمالي RSVP 👥</p>
                        <h2 className="fw-black text-primary mb-0">{analytics.rsvpStats.total}</h2>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                        <p className="text-success small fw-bold mb-1">سيحضر ✅</p>
                        <h2 className="fw-black text-success mb-0">{analytics.rsvpStats.confirmed}</h2>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                        <p className="text-danger small fw-bold mb-1">لن يحضر ❌</p>
                        <h2 className="fw-black text-danger mb-0">{analytics.rsvpStats.declined}</h2>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="bg-white p-4 rounded-celebration shadow-sm border text-center">
                        <p className="text-warning small fw-bold mb-1">ربما ⏳</p>
                        <h2 className="fw-black text-warning mb-0">{analytics.rsvpStats.pending}</h2>
                      </div>
                    </div>
                  </>
                )}

                {analytics?.hourlyData && (
                  <div className="col-12">
                    <div className="bg-white p-4 rounded-celebration shadow-sm border">
                      <p className="text-muted small fw-bold mb-3">نشاط الرفع (آخر 24 ساعة)</p>
                      <div className="d-flex align-items-end gap-2" style={{ height: '100px' }}>
                        {analytics.hourlyData.map((d, i) => (
                          <div
                            key={i}
                            className="bg-primary-dim rounded-top"
                            style={{
                              flex: 1,
                              height: `${Math.min(100, (d.count / (Math.max(...analytics.hourlyData.map(x => x.count)) || 1)) * 100)}%`,
                              minHeight: d.count > 0 ? '5px' : '0'
                            }}
                            title={`${d.hour}:00 - ${d.count} رفع`}
                          />
                        ))}
                      </div>
                      <div className="d-flex justify-content-between mt-2 text-muted small">
                        <span>24 ساعة مضت</span>
                        <span>الآن</span>
                      </div>
                    </div>
                  </div>
                )}
             </div>
          </div>
        ) : activeTab === "rsvp" ? (
          <div className="rsvp-view py-4 px-2">
             <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="badge bg-primary-dim text-primary rounded-pill px-3 py-2 fw-bold">
                   إجمالي الحضور: {rsvps.filter(r => r.status === 'confirmed').length}
                </div>
                <h4 className="fw-black mb-0 font-headline">قائمة الحضور (RSVP) 👥</h4>
             </div>
             <div className="bg-white rounded-celebration shadow-sm border overflow-hidden">
                <div className="table-responsive">
                   <table className="table table-hover align-middle mb-0 text-end">
                      <thead className="bg-light">
                         <tr>
                            <th className="px-4 py-3">الاسم</th>
                            <th className="px-4 py-3">الحالة</th>
                            <th className="px-4 py-3">الموبايل</th>
                            <th className="px-4 py-3">ملاحظات</th>
                            <th className="px-4 py-3">التاريخ</th>
                         </tr>
                      </thead>
                      <tbody>
                         {rsvps.length === 0 ? (
                           <tr><td colSpan="5" className="text-center py-5 text-muted">لا يوجد ردود بعد.</td></tr>
                         ) : rsvps.map((r, i) => (
                           <tr key={i}>
                              <td className="px-4 py-3">
                                 <div className="fw-bold">{r.guestName}</div>
                              </td>
                              <td className="px-4 py-3">
                                 <span className={`badge rounded-pill ${
                                   r.status === 'confirmed' ? 'bg-success-container text-success' :
                                   r.status === 'declined' ? 'bg-danger-container text-danger' :
                                   'bg-warning-container text-warning'
                                 }`}>
                                    {r.status === 'confirmed' ? 'سيحضر' : r.status === 'declined' ? 'لن يحضر' : 'ربما'}
                                 </span>
                              </td>
                              <td className="px-4 py-3 fw-bold">{r.phone || "-"}</td>
                              <td className="px-4 py-3 small text-muted text-truncate" style={{ maxWidth: '200px' }}>{r.note || "-"}</td>
                              <td className="px-4 py-3 small text-muted">{new Date(r.createdAt).toLocaleDateString('ar-EG')}</td>
                           </tr>
                         ))}
                      </tbody>
                   </table>
                </div>
             </div>
          </div>
        ) : activeTab === "invitation" ? (
          <div className="invitation-selection py-4 px-2 text-center">
            <h4
              className="fw-black mb-4"
              style={{ fontFamily: "var(--font-headline)" }}
            >
              اختر تصميم كارت الدعوة لمناسبتك ✨
            </h4>
            <div className="row g-4 justify-content-center">
              {Object.keys(templates).map((tid) => (
                <div key={tid} className="col-12 col-md-4 col-lg-3">
                  <motion.div
                    className={`card h-100 cursor-pointer border-3 ${
                      event.settings?.cardTemplate === tid
                        ? "border-primary shadow-lg"
                        : "border-transparent"
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const updatedEvent = { ...event };
                      if (!updatedEvent.settings) updatedEvent.settings = {};
                      updatedEvent.settings.cardTemplate = tid;
                      setEventDetails(updatedEvent);
                    }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="card-body p-2 d-flex flex-column">
                      <div
                        className="template-preview mb-3 rounded-4 border overflow-hidden bg-surface-variant d-flex align-items-center justify-content-center"
                        style={{ height: "200px" }}
                      >
                        <span className="fw-bold text-muted">
                          {templates[tid].name}
                        </span>
                      </div>
                      <h6 className="fw-bold mt-auto mb-0">
                        {templates[tid].name}
                      </h6>
                      {event.settings?.cardTemplate === tid && (
                        <div className="badge bg-primary mt-2 rounded-pill">
                          مختار حالياً ✅
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
            <div className="mt-5 d-flex gap-3 justify-content-center flex-wrap">
              <button
                className="btn btn-primary btn-lg px-5 fw-bold rounded-pill"
                onClick={updateEventDetails}
              >
                حفظ التصميم المختار ✅
              </button>
              <button
                className="btn btn-outline-dark btn-lg px-5 fw-bold rounded-pill"
                onClick={() =>
                  window.open(
                    `/event/${event._id || event.id}/invitation`,
                    "_blank"
                  )
                }
              >
                عرض وتحميل الكارت 🖨️
              </button>
            </div>
          </div>
        ) : (
          <div
            className="row g-3 g-md-4 overflow-auto p-1"
            style={{ maxHeight: "700px" }}
          >
            {loading && media.length === 0 && pendingMedia.length === 0 ? (
              [1, 2, 3, 4].map((n) => (
                <div key={n} className="col-6 col-md-4 col-lg-3">
                  <div
                    className="card h-100 shadow-sm border-0 bg-surface-variant animate-pulse"
                    style={{ height: "160px" }}
                  />
                </div>
              ))
            ) : (activeTab === "approved" ? media : pendingMedia).length ===
              0 ? (
              <div className="text-center text-muted py-5 border rounded-celebration bg-surface-variant w-100">
                <div className="display-4 mb-3">📸</div>
                <p className="mb-0 fs-5 fw-medium">لا توجد مشاركات هنا بعد.</p>
              </div>
            ) : (
              (activeTab === "approved" ? media : pendingMedia).map((item) => (
                <div key={item._id} className="col-6 col-md-4 col-lg-3">
                  <motion.div
                    className="card h-100 shadow-sm border-0 position-relative overflow-hidden"
                    whileHover={{ scale: window.innerWidth > 768 ? 1.02 : 1 }}
                    layout
                  >
                    <div
                      className="position-absolute top-0 end-0 p-2"
                      style={{ zIndex: 10 }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input shadow-sm"
                        style={{ width: "22px", height: "22px" }}
                        checked={selectedMediaIds.includes(item._id)}
                        onChange={() => toggleSelectMedia(item._id)}
                      />
                    </div>
                    <div
                      className="position-absolute bottom-0 end-0 p-2"
                      style={{ zIndex: 10 }}
                    >
                      <span
                        className={`badge rounded-pill ${
                          item.status === "approved"
                            ? "bg-success"
                            : "bg-secondary"
                        } opacity-90 shadow-sm`}
                      >
                        {item.status === "approved" ? "منشور" : "مؤرشف"}
                      </span>
                    </div>

                    {item.type === "photo" && (
                      <img
                        src={item.url}
                        alt="img"
                        className="card-img-top"
                        style={{ objectFit: "cover", height: "160px" }}
                      />
                    )}
                    {item.type === "video" && (
                      <div
                        className="d-flex align-items-center justify-content-center bg-dark"
                        style={{ height: "160px" }}
                      >
                        <span className="material-symbols-outlined text-white display-4">
                          play_circle
                        </span>
                      </div>
                    )}
                    {item.type === "voice" && (
                      <div
                        className="p-3 bg-light d-flex flex-column align-items-center justify-content-center"
                        style={{ height: "160px" }}
                      >
                        <div className="display-3 mb-2">🎙️</div>
                        <div className="small fw-bold">تسجيل صوتي</div>
                      </div>
                    )}
                    {item.type === "text" && (
                      <div
                        className="p-3 bg-light d-flex align-items-center justify-content-center text-center overflow-hidden"
                        style={{ height: "160px" }}
                      >
                        <small className="fw-bold fs-6 text-break p-2 italic">
                          "{item.content}"
                        </small>
                      </div>
                    )}

                    <div className="card-footer p-1 p-md-2 d-flex gap-1 gap-md-2 justify-content-center bg-white border-top">
                      <motion.button
                        className={`btn btn-sm ${
                          item.status === "archived"
                            ? "btn-success"
                            : "btn-outline-warning"
                        } px-2 px-md-3 rounded-pill fw-bold flex-grow-1`}
                        onClick={() =>
                          updateStatus(
                            item._id,
                            item.status === "archived" ? "approved" : "archived"
                          )
                        }
                        whileTap={{ scale: 0.9 }}
                      >
                        {item.status === "archived" ? "استعادة" : "أرشفة"}
                      </motion.button>
                      <motion.button
                        className="btn btn-sm btn-outline-danger px-2 px-md-3 rounded-pill fw-bold flex-grow-1"
                        onClick={() => deleteMedia(item._id)}
                        whileTap={{ scale: 0.9 }}
                      >
                        حذف
                      </motion.button>
                    </div>
                    <div className="position-absolute top-0 start-0 bg-primary text-white px-3 py-1 small rounded-bottom-end opacity-90 fw-bold shadow">
                      {item.guestName}
                    </div>
                  </motion.div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <style>{`
        .event-hero-banner {
          background: linear-gradient(135deg, var(--primary) 0%, #2d1e17 100%);
        }
        @keyframes pulse {
          0% { opacity: 0.5; }
          50% { opacity: 0.8; }
          100% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 1.5s infinite ease-in-out;
        }
      `}</style>
    </motion.div>
  );
};

export default EventManagement;
